import { useEffect, useRef, useState } from "preact/hooks"
import {
	type CropRect,
	type PhotoEdits,
	STRAIGHTEN_LIMIT_DEG,
	applyFrameTransform,
	decodeBitmap,
	defaultEdits,
	frameSize,
	rotateCropCCW,
	rotateCropCW,
} from "../lib/imageEdits"

// Fullscreen photo editor: rotate (90° steps), straighten (fine slider) and
// crop (draggable box with corner handles) in one surface. Bootstrap ships
// CSS-only in this app, so this is a hand-rolled fixed overlay, not a
// Bootstrap JS modal. The preview is drawn on a canvas with the exact same
// transform (applyFrameTransform) used by the final render in
// lib/imageEdits.ts, so what you see is what gets uploaded.

interface PhotoEditorProps {
	file: File
	initialEdits: PhotoEdits
	onApply: (edits: PhotoEdits) => void
	onCancel: () => void
}

const FULL_CROP: CropRect = { x: 0, y: 0, width: 1, height: 1 }
/** Minimum crop size, as a fraction of the frame per axis. */
const MIN_CROP = 0.1
/** Corner-handle hit radius and visual size, in CSS pixels. */
const HANDLE_HIT_PX = 24
const HANDLE_SIZE_PX = 14

type DragMode = "move" | "nw" | "ne" | "sw" | "se"

interface DragState {
	mode: DragMode
	startFrameX: number
	startFrameY: number
	startCrop: CropRect
}

const clamp = (v: number, lo: number, hi: number) =>
	Math.min(hi, Math.max(lo, v))

/** Treats a crop that covers (almost) the whole frame as "no crop". */
function normalizeCrop(crop: CropRect): CropRect | null {
	const eps = 0.005
	if (
		crop.x < eps &&
		crop.y < eps &&
		crop.x + crop.width > 1 - eps &&
		crop.y + crop.height > 1 - eps
	) {
		return null
	}
	return crop
}

export function PhotoEditor({
	file,
	initialEdits,
	onApply,
	onCancel,
}: PhotoEditorProps) {
	const [edits, setEdits] = useState<PhotoEdits>(initialEdits)
	const [bitmap, setBitmap] = useState<ImageBitmap | null>(null)
	const [loadError, setLoadError] = useState("")
	const [viewSize, setViewSize] = useState({ width: 0, height: 0 })

	const areaRef = useRef<HTMLDivElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const dragRef = useRef<DragState | null>(null)
	// CSS-pixels-per-frame-pixel scale of the current preview; set by the
	// draw effect and read by pointer handlers to map coordinates back.
	const fitRef = useRef(1)

	useEffect(() => {
		let cancelled = false
		let loaded: ImageBitmap | null = null
		decodeBitmap(file)
			.then((bm) => {
				if (cancelled) {
					bm.close()
					return
				}
				loaded = bm
				setBitmap(bm)
			})
			.catch(() => setLoadError("Could not load this photo for editing."))
		return () => {
			cancelled = true
			loaded?.close()
		}
	}, [file])

	// Track the preview area's size so the canvas refits on resize/rotation.
	useEffect(() => {
		const area = areaRef.current
		if (!area) return
		const ro = new ResizeObserver(() => {
			setViewSize({ width: area.clientWidth, height: area.clientHeight })
		})
		ro.observe(area)
		return () => ro.disconnect()
	}, [])

	// Lock body scroll while the editor is open; Escape cancels.
	useEffect(() => {
		const prevOverflow = document.body.style.overflow
		document.body.style.overflow = "hidden"
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onCancel()
		}
		document.addEventListener("keydown", onKey)
		return () => {
			document.body.style.overflow = prevOverflow
			document.removeEventListener("keydown", onKey)
		}
		// biome-ignore lint/correctness/useExhaustiveDependencies: onCancel is stable enough; re-binding per render is harmless but pointless
	}, [])

	// Redraw the preview whenever the image, edits or viewport change.
	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas || !bitmap || viewSize.width === 0 || viewSize.height === 0)
			return

		const frame = frameSize(bitmap.width, bitmap.height, edits.rotate)
		const pad = 16
		const fit = Math.min(
			(viewSize.width - pad * 2) / frame.width,
			(viewSize.height - pad * 2) / frame.height,
		)
		fitRef.current = fit
		const cssW = frame.width * fit
		const cssH = frame.height * fit
		const dpr = window.devicePixelRatio || 1
		canvas.width = Math.round(cssW * dpr)
		canvas.height = Math.round(cssH * dpr)
		canvas.style.width = `${cssW}px`
		canvas.style.height = `${cssH}px`

		const ctx = canvas.getContext("2d")
		if (!ctx) return

		// Map the context into frame coordinates: 1 unit = 1 frame pixel.
		ctx.setTransform(dpr * fit, 0, 0, dpr * fit, 0, 0)
		ctx.clearRect(0, 0, frame.width, frame.height)

		ctx.save()
		ctx.beginPath()
		ctx.rect(0, 0, frame.width, frame.height)
		ctx.clip()
		ctx.save()
		applyFrameTransform(ctx, bitmap.width, bitmap.height, edits)
		ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2)
		ctx.restore()

		// Crop overlay: veil outside the crop, border, thirds grid, handles.
		const crop = edits.crop ?? FULL_CROP
		const cx = crop.x * frame.width
		const cy = crop.y * frame.height
		const cw = crop.width * frame.width
		const ch = crop.height * frame.height

		ctx.fillStyle = "rgba(0, 0, 0, 0.55)"
		ctx.beginPath()
		ctx.rect(0, 0, frame.width, frame.height)
		ctx.rect(cx, cy, cw, ch)
		ctx.fill("evenodd")

		const px = 1 / fit // one CSS pixel, in frame units
		ctx.strokeStyle = "rgba(255, 255, 255, 0.35)"
		ctx.lineWidth = px
		ctx.beginPath()
		for (const t of [1 / 3, 2 / 3]) {
			ctx.moveTo(cx + cw * t, cy)
			ctx.lineTo(cx + cw * t, cy + ch)
			ctx.moveTo(cx, cy + ch * t)
			ctx.lineTo(cx + cw, cy + ch * t)
		}
		ctx.stroke()

		ctx.strokeStyle = "#fff"
		ctx.lineWidth = 2 * px
		ctx.strokeRect(cx, cy, cw, ch)

		ctx.fillStyle = "#fff"
		const hs = HANDLE_SIZE_PX * px
		for (const [hx, hy] of [
			[cx, cy],
			[cx + cw, cy],
			[cx, cy + ch],
			[cx + cw, cy + ch],
		]) {
			ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs)
		}
		ctx.restore()
	}, [bitmap, edits, viewSize])

	const toFrameCoords = (e: PointerEvent) => {
		const canvas = canvasRef.current
		if (!canvas) return { x: 0, y: 0 }
		const rect = canvas.getBoundingClientRect()
		return {
			x: (e.clientX - rect.left) / fitRef.current,
			y: (e.clientY - rect.top) / fitRef.current,
		}
	}

	const onPointerDown = (e: PointerEvent) => {
		if (!bitmap) return
		const frame = frameSize(bitmap.width, bitmap.height, edits.rotate)
		const pos = toFrameCoords(e)
		const crop = edits.crop ?? FULL_CROP
		const cx = crop.x * frame.width
		const cy = crop.y * frame.height
		const cw = crop.width * frame.width
		const ch = crop.height * frame.height

		const hit = HANDLE_HIT_PX / fitRef.current
		const corners: Array<[number, number, DragMode]> = [
			[cx, cy, "nw"],
			[cx + cw, cy, "ne"],
			[cx, cy + ch, "sw"],
			[cx + cw, cy + ch, "se"],
		]
		let mode: DragMode | null = null
		for (const [hx, hy, m] of corners) {
			if (Math.abs(pos.x - hx) <= hit && Math.abs(pos.y - hy) <= hit) {
				mode = m
				break
			}
		}
		if (
			!mode &&
			pos.x >= cx &&
			pos.x <= cx + cw &&
			pos.y >= cy &&
			pos.y <= cy + ch
		) {
			mode = "move"
		}
		if (!mode) return

		dragRef.current = {
			mode,
			startFrameX: pos.x,
			startFrameY: pos.y,
			startCrop: crop,
		}
		;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
	}

	const onPointerMove = (e: PointerEvent) => {
		const drag = dragRef.current
		if (!drag || !bitmap) return
		const frame = frameSize(bitmap.width, bitmap.height, edits.rotate)
		const pos = toFrameCoords(e)
		const dx = (pos.x - drag.startFrameX) / frame.width
		const dy = (pos.y - drag.startFrameY) / frame.height
		const c = drag.startCrop

		let next: CropRect
		if (drag.mode === "move") {
			next = {
				...c,
				x: clamp(c.x + dx, 0, 1 - c.width),
				y: clamp(c.y + dy, 0, 1 - c.height),
			}
		} else {
			// Each corner drags its own two edges; the opposite corner stays put.
			let x1 = c.x
			let y1 = c.y
			let x2 = c.x + c.width
			let y2 = c.y + c.height
			if (drag.mode === "nw" || drag.mode === "sw")
				x1 = clamp(c.x + dx, 0, x2 - MIN_CROP)
			else x2 = clamp(x2 + dx, x1 + MIN_CROP, 1)
			if (drag.mode === "nw" || drag.mode === "ne")
				y1 = clamp(c.y + dy, 0, y2 - MIN_CROP)
			else y2 = clamp(y2 + dy, y1 + MIN_CROP, 1)
			next = { x: x1, y: y1, width: x2 - x1, height: y2 - y1 }
		}
		setEdits((prev) => ({ ...prev, crop: next }))
	}

	const onPointerUp = () => {
		if (!dragRef.current) return
		dragRef.current = null
		setEdits((prev) => ({
			...prev,
			crop: prev.crop ? normalizeCrop(prev.crop) : null,
		}))
	}

	const rotateCW = () =>
		setEdits((prev) => ({
			...prev,
			rotate: ((prev.rotate + 90) % 360) as PhotoEdits["rotate"],
			crop: prev.crop ? rotateCropCW(prev.crop) : null,
		}))

	const rotateCCW = () =>
		setEdits((prev) => ({
			...prev,
			rotate: ((prev.rotate + 270) % 360) as PhotoEdits["rotate"],
			crop: prev.crop ? rotateCropCCW(prev.crop) : null,
		}))

	return (
		<div
			class="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column bg-body"
			// Disable text selection so long-press/drag on crop handles and the
			// canvas preview does not select surrounding UI on mobile browsers.
			style={{ zIndex: 1050, userSelect: "none" }}
			role="dialog"
			aria-modal="true"
			aria-label="Edit photo"
		>
			<div class="d-flex align-items-center justify-content-between p-2 border-bottom">
				<button
					type="button"
					class="btn btn-outline-secondary"
					style={{ minHeight: "44px" }}
					onClick={onCancel}
				>
					Cancel
				</button>
				<span class="fw-semibold">Edit photo</span>
				<button
					type="button"
					class="btn btn-primary"
					style={{ minHeight: "44px" }}
					onClick={() => onApply(edits)}
					disabled={!bitmap}
				>
					Apply
				</button>
			</div>

			<div
				ref={areaRef}
				class="flex-grow-1 position-relative overflow-hidden"
				style={{ backgroundColor: "#111" }}
			>
				{loadError ? (
					<div class="alert alert-danger m-3">{loadError}</div>
				) : (
					<canvas
						ref={canvasRef}
						class="position-absolute top-50 start-50 translate-middle"
						style={{ touchAction: "none", userSelect: "none" }}
						onPointerDown={onPointerDown}
						onPointerMove={onPointerMove}
						onPointerUp={onPointerUp}
						onPointerCancel={onPointerUp}
					/>
				)}
			</div>

			<div class="p-3 border-top vstack gap-3">
				<div class="d-flex align-items-center gap-3">
					<button
						type="button"
						class="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center"
						style={{ minWidth: "44px", minHeight: "44px" }}
						onClick={rotateCCW}
						aria-label="Rotate left"
					>
						<i class="bi bi-arrow-counterclockwise" aria-hidden="true" />
					</button>
					<div class="flex-grow-1">
						<label class="form-label small mb-1 d-flex justify-content-between">
							<span>Straighten</span>
							<span class="text-body-secondary">
								{edits.straighten.toFixed(1)}°
							</span>
						</label>
						<input
							type="range"
							class="form-range"
							min={-STRAIGHTEN_LIMIT_DEG}
							max={STRAIGHTEN_LIMIT_DEG}
							step={0.5}
							value={edits.straighten}
							onInput={(e) =>
								setEdits((prev) => ({
									...prev,
									straighten: Number((e.target as HTMLInputElement).value),
								}))
							}
						/>
					</div>
					<button
						type="button"
						class="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center"
						style={{ minWidth: "44px", minHeight: "44px" }}
						onClick={rotateCW}
						aria-label="Rotate right"
					>
						<i class="bi bi-arrow-clockwise" aria-hidden="true" />
					</button>
				</div>
				<button
					type="button"
					class="btn btn-outline-secondary btn-sm align-self-center"
					style={{ minHeight: "44px" }}
					onClick={() => setEdits(defaultEdits())}
				>
					Reset all
				</button>
			</div>
		</div>
	)
}
